import { getReportMetaContent } from '@/services/meta-service';
import { RouteLocationNormalizedLoaded } from 'vue-router';

export interface MetaTag {
  title: string;
  meta: {
    description: { name: 'description'; content: string };
    keywords: { name: 'keywords'; content: string };
    multi_words?: { name: 'multi_words'; content: string };
    'ai-content'?: { name: 'ai-content'; content: string };
    'og:title': { property: 'og:title'; content: string };
    'og:description': { property: 'og:description'; content: string };
    'og:type'?: { property: 'og:type'; content: string };
    'og:url'?: { property: 'og:url'; content: string };
    'og:image': { property: 'og:image'; content: string };
    'og:image:alt'?: { property: 'og:image:alt'; content: string };
    'og:locale'?: { property: 'og:locale'; content: string };
    'og:site_name'?: { property: 'og:site_name'; content: string };
    'twitter:card'?: { name: 'twitter:card'; content: string };
    'twitter:title'?: { name: 'twitter:title'; content: string };
    'twitter:description'?: { name: 'twitter:description'; content: string };
    'twitter:image'?: { name: 'twitter:image'; content: string };
    robots?: { name: 'robots'; content: string };
    viewport?: { name: 'viewport'; content: string };
  };
  link?: {
    canonical?: { rel: 'canonical'; href: string };
  };
  script?: {
    ldJson?: { type: 'application/ld+json'; innerHTML: string };
  };
  [key: string]: unknown;
} // <meta name="robots" content="noindex">

export const getMetaTags = async (route: RouteLocationNormalizedLoaded) => {
  const metaTags: Record<string, MetaTag> = {
    HomePage: {
      title: 'قيّم المواقع وشارك تجربتك | أكواد – منصة تقييم تجربة المستخدم',
      meta: {
        description: {
          name: 'description',
          content: 'قيّم وشارك تجربة المستخدم للمواقع والمنتجات الرقمية عبر أكواد. ابحث عن أي موقع، استعرض آراء المستخدمين، اكتشف المشاكل، وشارك تقييمك بسهولة.',
        },
        keywords: {
          name: 'keywords',
          content: 'تجربة المستخدم, تقييم المواقع, تقييم تجربة المستخدم, آراء المستخدمين, قابلية الاستخدام, تقييم المواقع السعودية, تحليل سلوك المستخدم',
        },
        'ai-content': {
          name: 'ai-content',
          content: 'صفحة التقييم في أكواد تتيح للمستخدمين البحث عن أي موقع، استعراض آراء وتقييمات تجربة المستخدم، واكتشاف المشكلات ومشاركتها لتحسين الأداء وتجربة المستخدم.',
        },
        'og:title': { property: 'og:title', content: 'قيّم المواقع وشارك تجربتك | أكواد – منصة تقييم تجربة المستخدم' },
        'og:description': {
          property: 'og:description',
          content: 'ابحث عن أي موقع، استعرض آراء المستخدمين واكتشف المشكلات، وشارك تقييمك بسهولة عبر أكواد.',
        },
        'og:type': { property: 'og:type', content: 'website' },
        'og:url': { property: 'og:url', content: 'https://akwad.sa/rating' },
        'og:image': { property: 'og:image', content: 'https://akwad.sa/assets/akwad-image.jpg' },
        'og:locale': { property: 'og:locale', content: 'ar_AR' },
        'og:site_name': { property: 'og:site_name', content: 'أكواد – أداة تقييم تجربة المستخدم' },
        'twitter:card': { name: 'twitter:card', content: 'summary_large_image' },
        'twitter:title': { name: 'twitter:title', content: 'قيّم المواقع وشارك تجربتك | أكواد – منصة تقييم تجربة المستخدم' },
        'twitter:description': { name: 'twitter:description', content: 'ابحث، استعرض التقييمات، واكتشف تجارب المستخدمين لأي موقع عبر أكواد.' },
        'twitter:image': { name: 'twitter:image', content: 'https://akwad.sa/assets/akwad-image.jpg' },
        robots: { name: 'robots', content: 'index, follow' },
        viewport: { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        ...(process.env.PROD_ENV === 'demo'
          ? { robots: { name: 'robots', content: 'noindex' } }
          : {}),
      },
      link: {
        canonical: { rel: 'canonical', href: 'https://akwad.sa/rating' },
      },
      script: {
        ldJson: {
          type: 'application/ld+json',
          innerHTML: JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': [
              {
                '@type': 'Organization',
                '@id': 'https://akwad.sa/#org',
                name: 'أكواد',
                alternateName: 'Akwad',
                url: 'https://akwad.sa/',
                logo: 'https://akwad.sa/assets/logo-CEm7vz4L.svg',
                sameAs: [
                  'https://www.linkedin.com/company/akwadux',
                  'https://www.instagram.com/akwadux',
                  'https://www.tiktok.com/@akwadux',
                  'https://x.com/AkwadUX',
                  'https://youtube.com/@akwadux'
                ]
              },
              {
                '@type': 'WebSite',
                '@id': 'https://akwad.sa/#website',
                url: 'https://akwad.sa/',
                name: 'أكواد',
                inLanguage: 'ar',
                publisher: { '@id': 'https://akwad.sa/#org' },
                potentialAction: {
                  '@type': 'SearchAction',
                  target: 'https://akwad.sa/rating?q={query}',
                  'query-input': 'required name=query'
                }
              },
              {
                '@type': 'WebPage',
                '@id': 'https://akwad.sa/rating#webpage',
                url: 'https://akwad.sa/rating',
                name: 'قيّم المواقع وشارك تجربتك',
                isPartOf: { '@id': 'https://akwad.sa/#website' },
                inLanguage: 'ar',
                description: 'استكشف تقييمات وتجارب المستخدمين لأي موقع، وشارك رأيك لكشف المشكلات وتحسين تجربة المستخدم عبر منصة أكواد.'
              },
              {
                '@type': 'FAQPage',
                '@id': 'https://akwad.sa/rating#faq',
                mainEntity: [
                  {
                    '@type': 'Question',
                    name: 'كيف أشارك تجربتي؟',
                    acceptedAnswer: {
                      '@type': 'Answer',
                      text: 'عن طريق استخدام أداة أكواد؛ يمكنك تحميلها من متجر جوجل أو البدء عبر الموقع ثم إدخال رابط الموقع وتعبئة نموذج التقييم.'
                    }
                  },
                  {
                    '@type': 'Question',
                    name: 'هل يتم مراجعة التعليقات قبل نشرها؟',
                    acceptedAnswer: {
                      '@type': 'Answer',
                      text: 'نعم، نراجع كل التعليقات للتأكد من التزامها بالمعايير قبل نشرها ضمن النتائج.'
                    }
                  },
                  {
                    '@type': 'Question',
                    name: 'كيف أنشئ حساب؟',
                    acceptedAnswer: {
                      '@type': 'Answer',
                      text: 'بسيطة! اضغط على "إنشاء حساب" واملأ البيانات المطلوبة لتفعيل حسابك.'
                    }
                  },
                  {
                    '@type': 'Question',
                    name: 'وش نوع التعليقات اللي أقدر أنشرها؟',
                    acceptedAnswer: {
                      '@type': 'Answer',
                      text: 'نرحّب بالانطباعات والملاحظات البنّاءة حول سهولة الاستخدام والمشكلات والاقتراحات. تُستبعد التعليقات المخالفة للسياسات أو غير ذات صلة.'
                    }
                  },
                  {
                    '@type': 'Question',
                    name: 'هل أقدر أعدل تعليقي؟',
                    acceptedAnswer: {
                      '@type': 'Answer',
                      text: 'نعم، تقدر تعدل تعليقك بعد النشر في أي وقت من خلال لوحة التحكم.'
                    }
                  },
                  {
                    '@type': 'Question',
                    name: 'وش نوع المواقع اللي أقدر أقيمها؟',
                    acceptedAnswer: {
                      '@type': 'Answer',
                      text: 'تقدر تقيم أي موقع أو منتج رقمي ضمن السياسات المعتمدة في أكواد.'
                    }
                  }
                ]
              }
            ]
          }),
        },
      },
    },
    NewHomePage: {
      // Basic meta tags
      title:
        'أكواد – أداة تقييم تجربة المستخدم للمواقع والمنتجات الرقمية | تقارير وتحليلات احترافية',

      meta: {
        description: {
          name: 'description',
          content:
            'أكواد أداة سعودية متخصصة في تقييم تجربة المستخدم للمواقع والمنتجات الرقمية، تقدم تقارير وتحليلات احترافية لسلوك الزوار وحلول لتحسين واجهة وتجربة المستخدم.',
        },
        keywords: {
          name: 'keywords',
          content:
            'تجربة المستخدم, تقييم تجربة المستخدم, أداة تجربة المستخدم, اختبار قابلية الاستخدام, تحسين واجهة المستخدم, تحليل سلوك الزوار, تحسين تجربة المستخدم',
        },
        'ai-content': {
          name: 'ai-content',
          content:
            'أكواد أداة سعودية لتقييم وتحسين تجربة المستخدم للمواقع والمنتجات الرقمية، تقدم تقارير وتحليلات احترافية لسلوك الزوار مدعومة بالذكاء الاصطناعي، بهدف رفع الأداء وزيادة رضا العملاء.',
        },

        // Open Graph tags for social media
        'og:title': {
          property: 'og:title',
          content: 'أكواد – أداة تقييم تجربة المستخدم للمواقع والمنتجات الرقمية',
        },
        'og:description': {
          property: 'og:description',
          content:
            'أكواد أداة سعودية لتقييم تجربة المستخدم، تقارير وتحليلات احترافية لتحسين أداء المواقع والمنتجات الرقمية.',
        },
        'og:type': {
          property: 'og:type',
          content: 'website',
        },
        'og:url': {
          property: 'og:url',
          content: 'https://akwad.sa/',
        },
        'og:image': {
          property: 'og:image',
          content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg',
        },
        'og:locale': {
          property: 'og:locale',
          content: 'ar_AR',
        },
        'og:site_name': {
          property: 'og:site_name',
          content: 'أكواد – أداة تقييم تجربة المستخدم',
        },

        // Twitter Card tags
        'twitter:card': {
          name: 'twitter:card',
          content: 'summary_large_image',
        },
        'twitter:title': {
          name: 'twitter:title',
          content: 'أكواد – أداة تقييم تجربة المستخدم للمواقع والمنتجات الرقمية',
        },
        'twitter:description': {
          name: 'twitter:description',
          content:
            'أداة سعودية لتحسين تجربة المستخدم عبر تقييم احترافي للمواقع والمنتجات الرقمية مع تقارير وتحليلات متقدمة.',
        },
        'twitter:image': {
          name: 'twitter:image',
          content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg',
        },

        // Additional SEO tags
        robots: {
          name: 'robots',
          content: 'index, follow',
        },
        viewport: {
          name: 'viewport',
          content: 'width=device-width, initial-scale=1',
        },
      },

      // Link tags
      link: {
        canonical: {
          rel: 'canonical',
          href: 'https://akwad.sa/',
        },
      },

      // JSON-LD structured data
      script: {
        ldJson: {
          type: 'application/ld+json',
          innerHTML: JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': [
              {
                '@type': 'Organization',
                '@id': 'https://akwad.sa/#org',
                name: 'أكواد',
                alternateName: 'Akwad',
                url: 'https://akwad.sa/',
                logo: 'https://akwad.sa/logo.jpg',
                description:
                  'أكواد أداة سعودية لتقييم وتحسين تجربة المستخدم للمواقع والمنتجات الرقمية عبر تقارير وتحليلات احترافية مدعومة بالذكاء الاصطناعي.',
                sameAs: [
                  'https://www.linkedin.com/company/akwadux',
                  'https://www.instagram.com/akwadux',
                  'https://www.tiktok.com/@akwadux',
                  'https://x.com/AkwadUX',
                  'https://youtube.com/@akwadux',
                ],
                contactPoint: [
                  {
                    '@type': 'ContactPoint',
                    contactType: 'customer support',
                    email: 'info@akwad.sa',
                    areaServed: 'SA',
                    availableLanguage: ['ar', 'en'],
                  },
                ],
              },
              {
                '@type': 'WebSite',
                '@id': 'https://akwad.sa/#website',
                url: 'https://akwad.sa/',
                name: 'أكواد',
                inLanguage: 'ar',
                publisher: { '@id': 'https://akwad.sa/#org' },
                potentialAction: {
                  '@type': 'SearchAction',
                  target: 'https://akwad.sa/search?q={query}',
                  'query-input': 'required name=query',
                },
              },
              {
                '@type': 'SoftwareApplication',
                '@id': 'https://akwad.sa/#app',
                name: 'أكواد – أداة تقييم تجربة المستخدم',
                operatingSystem: 'Web',
                applicationCategory: 'BusinessApplication',
                inLanguage: 'ar',
                url: 'https://akwad.sa/',
                description:
                  'منصّة سعودية لاختبارات قابلية الاستخدام وتحليل سلوك الزوار وتقارير تحسين تجربة وواجهة المستخدم.',
                publisher: { '@id': 'https://akwad.sa/#org' },
              },
              {
                '@type': 'Service',
                '@id': 'https://akwad.sa/#ux-service',
                serviceType: 'تقييم تجربة المستخدم',
                provider: { '@id': 'https://akwad.sa/#org' },
                areaServed: ['SA', 'GCC'],
                audience: {
                  '@type': 'BusinessAudience',
                  name: 'الشركات والجهات التي تطور مواقع وتطبيقات',
                },
                description:
                  'اختبارات تفاعلية، تحليلات كمية ونوعية لسلوك المستخدم، وتقارير احترافية لتحسين واجهة وتجربة المستخدم.',
              },
              {
                '@type': 'FAQPage',
                '@id': 'https://akwad.sa/#faq',
                mainEntity: [
                  {
                    '@type': 'Question',
                    name: 'كيف أشارك تجربتي؟',
                    acceptedAnswer: {
                      '@type': 'Answer',
                      text: 'عن طريق استخدام أداة أكواد؛ يمكنك تحميلها من متجر جوجل أو البدء عبر الموقع.',
                    },
                  },
                  {
                    '@type': 'Question',
                    name: 'هل يتم مراجعة التعليقات قبل نشرها؟',
                    acceptedAnswer: {
                      '@type': 'Answer',
                      text: 'نعم، نراجع كل التعليقات للتأكد من التزامها بالمعايير قبل نشرها ضمن النتائج.',
                    },
                  },
                  {
                    '@type': 'Question',
                    name: 'كيف أنشئ حساب؟',
                    acceptedAnswer: {
                      '@type': 'Answer',
                      text: 'بسيطة! اضغط على "إنشاء حساب" واملأ البيانات المطلوبة لتفعيل حسابك.',
                    },
                  },
                  {
                    '@type': 'Question',
                    name: 'وش نوع التعليقات اللي أقدر أنشرها؟',
                    acceptedAnswer: {
                      '@type': 'Answer',
                      text: 'نرحّب بالتعليقات المتعلقة بالانطباعات، المشكلات، الاقتراحات، وسهولة الاستخدام. تُستبعد التعليقات المخالفة للسياسات أو غير ذات صلة.',
                    },
                  },
                  {
                    '@type': 'Question',
                    name: 'هل أقدر أعدل تعليقي؟',
                    acceptedAnswer: {
                      '@type': 'Answer',
                      text: 'نعم، تقدر تعدل تعليقك بعد النشر في أي وقت من خلال لوحة التحكم.',
                    },
                  },
                  {
                    '@type': 'Question',
                    name: 'وش نوع المواقع اللي أقدر أقيمها؟',
                    acceptedAnswer: {
                      '@type': 'Answer',
                      text: 'تقدر تقيم أي موقع أو منتج رقمي تبغاه ضمن السياسات المعتمدة في أكواد.',
                    },
                  },
                ],
              },
            ],
          }),
        },
      },
    },
    LabPage: {
      title: 'مختبر تجربة المستخدم | أكواد – اختبارات تفاعلية وتحليلات احترافية',
      meta: {
        description: {
          name: 'description',
          content: 'استكشف مختبر أكواد لاختبارات تجربة المستخدم التفاعلية. اختبر قابلية الاستخدام، حلل سلوك المستخدمين، واحصل على تقارير احترافية لتحسين واجهة موقعك.',
        },
        keywords: {
          name: 'keywords',
          content: 'مختبر تجربة المستخدم, اختبار قابلية الاستخدام, تحليل سلوك المستخدم, اختبارات تفاعلية, تحسين واجهة المستخدم, تقارير UX, أكواد',
        },
        'ai-content': {
          name: 'ai-content',
          content: 'مختبر أكواد يوفر بيئة متقدمة لاختبارات تجربة المستخدم التفاعلية، مع أدوات تحليل سلوك المستخدم وتقارير احترافية لتحسين واجهة وتجربة المواقع والمنتجات الرقمية.',
        },
        'og:title': { property: 'og:title', content: 'مختبر تجربة المستخدم | أكواد – اختبارات تفاعلية وتحليلات احترافية' },
        'og:description': {
          property: 'og:description',
          content: 'استكشف مختبر أكواد لاختبارات تجربة المستخدم التفاعلية. اختبر قابلية الاستخدام، حلل سلوك المستخدمين، واحصل على تقارير احترافية.',
        },
        'og:type': { property: 'og:type', content: 'website' },
        'og:url': { property: 'og:url', content: 'https://akwad.sa/lab' },
        'og:image': { property: 'og:image', content: 'https://akwad.sa/assets/akwad-lab-image.jpg' },
        'og:image:alt': { property: 'og:image:alt', content: 'مختبر أكواد لاختبارات تجربة المستخدم' },
        'og:locale': { property: 'og:locale', content: 'ar_AR' },
        'og:site_name': { property: 'og:site_name', content: 'أكواد – أداة تقييم تجربة المستخدم' },
        'twitter:card': { name: 'twitter:card', content: 'summary_large_image' },
        'twitter:title': { name: 'twitter:title', content: 'مختبر تجربة المستخدم | أكواد – اختبارات تفاعلية وتحليلات احترافية' },
        'twitter:description': { name: 'twitter:description', content: 'استكشف مختبر أكواد لاختبارات تجربة المستخدم التفاعلية مع تحليلات سلوك المستخدم وتقارير احترافية.' },
        'twitter:image': { name: 'twitter:image', content: 'https://akwad.sa/assets/akwad-lab-image.jpg' },
        robots: { name: 'robots', content: 'index, follow' },
        viewport: { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        ...(process.env.PROD_ENV === 'demo'
          ? { robots: { name: 'robots', content: 'noindex' } }
          : {}),
      },
      link: {
        canonical: { rel: 'canonical', href: 'https://akwad.sa/lab' },
      },
      script: {
        ldJson: {
          type: 'application/ld+json',
          innerHTML: JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': [
              {
                '@type': 'Organization',
                '@id': 'https://akwad.sa/#org',
                name: 'أكواد',
                alternateName: 'Akwad',
                url: 'https://akwad.sa/',
                logo: 'https://akwad.sa/assets/logo-CEm7vz4L.svg',
                sameAs: [
                  'https://www.linkedin.com/company/akwadux',
                  'https://www.instagram.com/akwadux',
                  'https://www.tiktok.com/@akwadux',
                  'https://x.com/AkwadUX',
                  'https://youtube.com/@akwadux'
                ]
              },
              {
                '@type': 'WebSite',
                '@id': 'https://akwad.sa/#website',
                url: 'https://akwad.sa/',
                name: 'أكواد',
                inLanguage: 'ar',
                publisher: { '@id': 'https://akwad.sa/#org' },
                potentialAction: {
                  '@type': 'SearchAction',
                  target: 'https://akwad.sa/lab?q={query}',
                  'query-input': 'required name=query'
                }
              },
              {
                '@type': 'WebPage',
                '@id': 'https://akwad.sa/lab#webpage',
                url: 'https://akwad.sa/lab',
                name: 'مختبر تجربة المستخدم',
                isPartOf: { '@id': 'https://akwad.sa/#website' },
                inLanguage: 'ar',
                description: 'مختبر متقدم لاختبارات تجربة المستخدم التفاعلية مع أدوات تحليل سلوك المستخدم وتقارير احترافية لتحسين واجهة المواقع والمنتجات الرقمية.'
              },
              {
                '@type': 'Service',
                '@id': 'https://akwad.sa/lab#ux-lab-service',
                serviceType: 'مختبر اختبار تجربة المستخدم',
                provider: { '@id': 'https://akwad.sa/#org' },
                name: 'مختبر أكواد لاختبارات تجربة المستخدم',
                description: 'خدمة متقدمة لاختبارات تجربة المستخدم التفاعلية مع تحليل سلوك المستخدم وتقارير احترافية',
                areaServed: ['SA', 'GCC'],
                audience: {
                  '@type': 'BusinessAudience',
                  name: 'الشركات والمطورين الذين يريدون تحسين تجربة المستخدم'
                }
              },
              {
                '@type': 'HowTo',
                '@id': 'https://akwad.sa/lab#how-to-use-lab',
                name: 'كيفية استخدام مختبر أكواد',
                description: 'دليل شامل لاستخدام مختبر أكواد لاختبارات تجربة المستخدم',
                step: [
                  {
                    '@type': 'HowToStep',
                    name: 'إنشاء مشروع اختبار',
                    text: 'ابدأ بإنشاء مشروع جديد في مختبر أكواد وحدد الموقع أو التطبيق المراد اختباره'
                  },
                  {
                    '@type': 'HowToStep',
                    name: 'إعداد معايير الاختبار',
                    text: 'حدد معايير الاختبار والمهام المطلوبة من المستخدمين المشاركين'
                  },
                  {
                    '@type': 'HowToStep',
                    name: 'تشغيل الاختبارات',
                    text: 'شغل الاختبارات التفاعلية وجمع البيانات من المستخدمين المشاركين'
                  },
                  {
                    '@type': 'HowToStep',
                    name: 'تحليل النتائج',
                    text: 'احصل على تقارير تحليلية مفصلة لسلوك المستخدم وتوصيات التحسين'
                  }
                ]
              },
              {
                '@type': 'FAQPage',
                '@id': 'https://akwad.sa/lab#faq',
                mainEntity: [
                  {
                    '@type': 'Question',
                    name: 'كيف يعمل مختبر أكواد؟',
                    acceptedAnswer: {
                      '@type': 'Answer',
                      text: 'مختبر أكواد يوفر بيئة تفاعلية لاختبار تجربة المستخدم مع أدوات تحليل متقدمة وتقارير احترافية لتحسين واجهة المواقع والمنتجات الرقمية.'
                    }
                  },
                  {
                    '@type': 'Question',
                    name: 'هل يمكنني اختبار تطبيقات الموبايل؟',
                    acceptedAnswer: {
                      '@type': 'Answer',
                      text: 'نعم، مختبر أكواد يدعم اختبار تجربة المستخدم للمواقع والمنتجات الرقمية على جميع الأجهزة والمنصات.'
                    }
                  },
                  {
                    '@type': 'Question',
                    name: 'كم عدد المستخدمين المطلوب للاختبار؟',
                    acceptedAnswer: {
                      '@type': 'Answer',
                      text: 'نوصي بـ 5-10 مستخدمين لكل اختبار للحصول على نتائج موثوقة، مع إمكانية زيادة العدد حسب احتياجات المشروع.'
                    }
                  },
                  {
                    '@type': 'Question',
                    name: 'هل التقارير تتضمن توصيات للتحسين؟',
                    acceptedAnswer: {
                      '@type': 'Answer',
                      text: 'نعم، التقارير تتضمن تحليل مفصل لسلوك المستخدم مع توصيات عملية لتحسين تجربة المستخدم وواجهة الموقع.'
                    }
                  },
                  {
                    '@type': 'Question',
                    name: 'كم تستغرق مدة الاختبار؟',
                    acceptedAnswer: {
                      '@type': 'Answer',
                      text: 'مدة الاختبار تتراوح بين 15-30 دقيقة لكل مستخدم، مع إمكانية تخصيص المدة حسب تعقيد المهام المطلوبة.'
                    }
                  },
                  {
                    '@type': 'Question',
                    name: 'هل يمكنني مشاركة النتائج مع فريق العمل؟',
                    acceptedAnswer: {
                      '@type': 'Answer',
                      text: 'نعم، يمكنك تصدير التقارير ومشاركتها مع فريق العمل أو العملاء بسهولة عبر منصة أكواد.'
                    }
                  }
                ]
              }
            ]
          }),
        },
      },
    },
    ExpertPage: {
      title: 'انضم كمقيم خبير UX | أكواد – حوّل خبرتك إلى دخل حقيقي',
      meta: {
        description: {
          name: 'description',
          content: 'سجّل كمقيم خبير في أكواد وابدأ تقييم المواقع والمنتجات الرقمية باحتراف. التقط الطلب، نفّذ التقييم عبر الأداة، واكسب مقابل كل تقييم مكتمل.',
        },
        keywords: {
          name: 'keywords',
          content: 'تجربة المستخدم, تقييم تجربة المستخدم, مقيم خبير, UX Expert, وظائف UX, تقييم المواقع, قابلية الاستخدام, مراجعات احترافية',
        },
        'ai-content': {
          name: 'ai-content',
          content: 'صفحة الخبير في أكواد تمكّن المختصين في تجربة المستخدم من التسجيل كمقيمين معتمدين، تنفيذ تقييمات احترافية للمواقع والمنتجات الرقمية، وكسب دخل مقابل كل تقييم مكتمل.',
        },
        'og:title': { property: 'og:title', content: 'انضم كمقيم خبير UX | أكواد – حوّل خبرتك إلى دخل حقيقي' },
        'og:description': {
          property: 'og:description',
          content: 'سجّل كمقيم خبير، نفّذ تقييمات احترافية لتجربة المستخدم، واكسب مقابل كل تقييم مكتمل عبر أداة أكواد.',
        },
        'og:type': { property: 'og:type', content: 'website' },
        'og:url': { property: 'og:url', content: 'https://akwad.sa/expert' },
        'og:image': { property: 'og:image', content: 'https://akwad.sa/assets/akwad-image.jpg' },
        'og:image:alt': { property: 'og:image:alt', content: 'شعار ومنتج أكواد لتقييم تجربة المستخدم' },
        'og:locale': { property: 'og:locale', content: 'ar_AR' },
        'og:site_name': { property: 'og:site_name', content: 'أكواد – أداة تقييم تجربة المستخدم' },
        'twitter:card': { name: 'twitter:card', content: 'summary_large_image' },
        'twitter:title': { name: 'twitter:title', content: 'انضم كمقيم خبير UX | أكواد – حوّل خبرتك إلى دخل حقيقي' },
        'twitter:description': { name: 'twitter:description', content: 'سجّل كمقيم خبير، نفّذ تقييمات UX باحتراف، واكسب مقابل كل تقييم مكتمل عبر أكواد.' },
        'twitter:image': { name: 'twitter:image', content: 'https://akwad.sa/assets/akwad-image.jpg' },
        robots: { name: 'robots', content: 'index, follow' },
        viewport: { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        ...(process.env.PROD_ENV === 'demo'
          ? { robots: { name: 'robots', content: 'noindex' } }
          : {}),
      },
      link: {
        canonical: { rel: 'canonical', href: 'https://akwad.sa/expert' },
      },
      script: {
        ldJson: {
          type: 'application/ld+json',
          innerHTML: JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': [
              {
                '@type': 'Organization',
                '@id': 'https://akwad.sa/#org',
                name: 'أكواد',
                alternateName: 'Akwad',
                url: 'https://akwad.sa/',
                logo: 'https://akwad.sa/assets/logo-CEm7vz4L.svg',
                sameAs: [
                  'https://www.linkedin.com/company/akwadux',
                  'https://www.instagram.com/akwadux',
                  'https://www.tiktok.com/@akwadux',
                  'https://x.com/AkwadUX',
                  'https://youtube.com/@akwadux'
                ]
              },
              {
                '@type': 'WebSite',
                '@id': 'https://akwad.sa/#website',
                url: 'https://akwad.sa/',
                name: 'أكواد',
                inLanguage: 'ar',
                publisher: { '@id': 'https://akwad.sa/#org' },
                potentialAction: {
                  '@type': 'SearchAction',
                  target: 'https://akwad.sa/search?q={query}',
                  'query-input': 'required name=query'
                }
              },
              {
                '@type': 'WebPage',
                '@id': 'https://akwad.sa/expert#webpage',
                url: 'https://akwad.sa/expert',
                name: 'انضم كمقيم خبير UX',
                isPartOf: { '@id': 'https://akwad.sa/#website' },
                inLanguage: 'ar',
                description: 'سجّل كمقيم معتمد وابدأ تقييم المواقع الرقمية عبر أداة أكواد؛ التقط الطلب، نفّذ التقييم، واكسب مقابل كل تقييم مكتمل.'
              },
              {
                '@type': 'HowTo',
                '@id': 'https://akwad.sa/expert#howto',
                name: 'كيف تبدأ بالتقييم في أكواد',
                inLanguage: 'ar',
                description: 'اتبع الخطوات لبدء التقييم كمقيم خبير في أكواد.',
                step: [
                  {
                    '@type': 'HowToStep',
                    position: 1,
                    name: 'التقط الطلب',
                    text: 'يُعرض الطلب على المقيمين المعتمدين حسب المجال والتخصص داخل المنصة.'
                  },
                  {
                    '@type': 'HowToStep',
                    position: 2,
                    name: 'نفّذ التقييم',
                    text: 'نفّذ التقييم عبر أداة أكواد بناءً على تخصصك وقدّم ملاحظات وتوصيات ضمن تجربة منظمة.'
                  },
                  {
                    '@type': 'HowToStep',
                    position: 3,
                    name: 'اكسب المال',
                    text: 'كل تقييم مكتمل يُكافأ برصيد ويُسجّل ضمن ملفك المهني داخل المنصة.'
                  }
                ]
              },
              {
                '@type': 'FAQPage',
                '@id': 'https://akwad.sa/expert#faq',
                mainEntity: [
                  {
                    '@type': 'Question',
                    name: 'كيف أشارك تجربتي؟',
                    acceptedAnswer: {
                      '@type': 'Answer',
                      text: 'عن طريق استخدام أداة أكواد؛ يمكنك تحميلها من متجر جوجل أو البدء عبر الموقع ثم تنفيذ التقييم بحسب التخصص.'
                    }
                  },
                  {
                    '@type': 'Question',
                    name: 'هل يتم مراجعة التعليقات قبل نشرها؟',
                    acceptedAnswer: {
                      '@type': 'Answer',
                      text: 'نعم، تتم المراجعة للتأكد من الالتزام بالسياسات والجودة قبل اعتمادها في التقارير.'
                    }
                  },
                  {
                    '@type': 'Question',
                    name: 'كيف أنشئ حساب؟',
                    acceptedAnswer: {
                      '@type': 'Answer',
                      text: 'اضغط على "إنشاء حساب" واملأ البيانات المطلوبة، ثم قدّم طلب الانضمام كمقيم خبير.'
                    }
                  },
                  {
                    '@type': 'Question',
                    name: 'وش نوع التعليقات اللي أقدر أنشرها؟',
                    acceptedAnswer: {
                      '@type': 'Answer',
                      text: 'الملاحظات المهنية حول سهولة الاستخدام، المشكلات، الاقتراحات، ودلالات التصميم؛ تُستبعد التعليقات غير ذات صلة.'
                    }
                  },
                  {
                    '@type': 'Question',
                    name: 'هل أقدر أعدل تعليقي؟',
                    acceptedAnswer: {
                      '@type': 'Answer',
                      text: 'نعم، تقدر تعدّل تقييماتك وملاحظاتك بعد النشر من لوحة التحكم.'
                    }
                  }
                ]
              }
            ]
          }),
        },
      },
    },
    JoinUsPage: {
      title: 'سجل اهتمامك بحلول أكواد | شريكك الموثوق لتحسين تجربة المستخدم',
      meta: {
        description: {
          name: 'description',
          content: 'سجل اهتمامك بحلول أكواد لتقييم وتحسين تجربة المستخدم. شريكك الموثوق لتحسين تجربة المستخدم للمواقع والمنتجات الرقمية. سنتواصل معك في أقرب وقت.',
        },
        keywords: {
          name: 'keywords',
          content: 'أكواد, حلول تجربة المستخدم, تحسين UX, تقييم تجربة المستخدم, حلول رقمية, شريك تقني, تحسين المواقع',
        },
        'ai-content': {
          name: 'ai-content',
          content: 'سجل اهتمامك بحلول أكواد لتقييم وتحسين تجربة المستخدم. نحن شريكك الموثوق لتحسين تجربة المستخدم للمواقع والمنتجات الرقمية. سنتواصل معك في أقرب وقت.',
        },
        'og:title': {
          property: 'og:title',
          content: 'سجل اهتمامك بحلول أكواد | شريكك الموثوق لتحسين تجربة المستخدم',
        },
        'og:description': {
          property: 'og:description',
          content: 'سجل اهتمامك بحلول أكواد لتقييم وتحسين تجربة المستخدم. شريكك الموثوق لتحسين تجربة المستخدم للمواقع والمنتجات الرقمية.',
        },
        'og:type': { property: 'og:type', content: 'website' },
        'og:url': { property: 'og:url', content: 'https://akwad.sa/register-interest' },
        'og:image': { property: 'og:image', content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg' },
        'og:locale': { property: 'og:locale', content: 'ar_AR' },
        'og:site_name': { property: 'og:site_name', content: 'أكواد – أداة تقييم تجربة المستخدم' },
        'twitter:card': { name: 'twitter:card', content: 'summary_large_image' },
        'twitter:title': {
          name: 'twitter:title',
          content: 'سجل اهتمامك بحلول أكواد | شريكك الموثوق لتحسين تجربة المستخدم',
        },
        'twitter:description': {
          name: 'twitter:description',
          content: 'سجل اهتمامك بحلول أكواد لتقييم وتحسين تجربة المستخدم. سنتواصل معك في أقرب وقت.',
        },
        'twitter:image': { name: 'twitter:image', content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg' },
        robots: { name: 'robots', content: 'index, follow' },
        viewport: { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        ...(process.env.PROD_ENV === 'demo'
          ? { robots: { name: 'robots', content: 'noindex' } }
          : {}),
      },
      link: {
        canonical: { rel: 'canonical', href: 'https://akwad.sa/register-interest' },
      },
      script: {
        ldJson: {
          type: 'application/ld+json',
          innerHTML: JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': [
              {
                '@type': 'Organization',
                '@id': 'https://akwad.sa/#org',
                name: 'أكواد',
                alternateName: 'Akwad',
                url: 'https://akwad.sa/',
                logo: 'https://akwad.sa/assets/logo-CEm7vz4L.svg',
                sameAs: [
                  'https://www.linkedin.com/company/akwadux',
                  'https://www.instagram.com/akwadux',
                  'https://www.tiktok.com/@akwadux',
                  'https://x.com/AkwadUX',
                  'https://youtube.com/@akwadux'
                ]
              },
              {
                '@type': 'WebPage',
                '@id': 'https://akwad.sa/register-interest#webpage',
                url: 'https://akwad.sa/register-interest',
                name: 'سجل اهتمامك بحلول أكواد',
                isPartOf: { '@id': 'https://akwad.sa/#website' },
                inLanguage: 'ar',
                description: 'سجل اهتمامك بحلول أكواد لتقييم وتحسين تجربة المستخدم. شريكك الموثوق لتحسين تجربة المستخدم للمواقع والمنتجات الرقمية.',
              },
              {
                '@type': 'ContactPage',
                '@id': 'https://akwad.sa/register-interest#contact',
                name: 'سجل اهتمامك بحلول أكواد',
                description: 'سجل اهتمامك بحلول أكواد لتقييم وتحسين تجربة المستخدم. سنتواصل معك في أقرب وقت.',
                inLanguage: 'ar',
              }
            ]
          }),
        },
      },
    },
    ProfilePage: {
      title: 'أكواد | الملف الشخصي',
      meta: {
        description: {
          name: 'description',
          content: 'إدارة ملفك الشخصي وتفضيلاتك في منصة أكواد لتقييم تجربة المستخدم',
        },
        keywords: {
          name: 'keywords',
          content: 'الملف الشخصي, إعدادات الحساب, تفضيلات المستخدم, أكواد',
        },
        multi_words: {
          name: 'multi_words',
          content: 'profile, account, settings, akwad',
        },
        'og:title': { property: 'og:title', content: 'أكواد | الملف الشخصي' },
        'og:description': {
          property: 'og:description',
          content: 'إدارة ملفك الشخصي وتفضيلاتك في منصة أكواد لتقييم تجربة المستخدم',
        },
        'og:type': { property: 'og:type', content: 'website' },
        'og:url': { property: 'og:url', content: 'https://akwad.sa/profile' },
        'og:image': { property: 'og:image', content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg' },
        'og:locale': { property: 'og:locale', content: 'ar_AR' },
        'og:site_name': { property: 'og:site_name', content: 'أكواد – أداة تقييم تجربة المستخدم' },
        'twitter:card': { name: 'twitter:card', content: 'summary_large_image' },
        'twitter:title': { name: 'twitter:title', content: 'أكواد | الملف الشخصي' },
        'twitter:description': { name: 'twitter:description', content: 'إدارة ملفك الشخصي وتفضيلاتك في منصة أكواد لتقييم تجربة المستخدم' },
        'twitter:image': { name: 'twitter:image', content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg' },
        robots: { name: 'robots', content: 'index, follow' },
        viewport: { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        ...(process.env.PROD_ENV === 'demo'
          ? { robots: { name: 'robots', content: 'noindex' } }
          : {}),
      },
      link: {
        canonical: { rel: 'canonical', href: 'https://akwad.sa/profile' },
      },
    },
    VerifyAccountPage: {
      title: 'أكواد | توثيق الموقع',
      meta: {
        description: {
          name: 'description',
          content: 'إدارة ملف عملك التجاري في منصة أكواد لتقييم تجربة المستخدم',
        },
        keywords: {
          name: 'keywords',
          content: 'الملف التجاري, إدارة الأعمال, توثيق الموقع, أكواد',
        },
        multi_words: {
          name: 'multi_words',
          content: 'business, profile, management, akwad',
        },
        'og:title': { property: 'og:title', content: 'أكواد | توثيق الموقع' },
        'og:description': {
          property: 'og:description',
          content: 'إدارة ملف عملك التجاري في منصة أكواد لتقييم تجربة المستخدم',
        },
        'og:type': { property: 'og:type', content: 'website' },
        'og:url': { property: 'og:url', content: 'https://akwad.sa/verify-account' },
        'og:image': { property: 'og:image', content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg' },
        'og:locale': { property: 'og:locale', content: 'ar_AR' },
        'og:site_name': { property: 'og:site_name', content: 'أكواد – أداة تقييم تجربة المستخدم' },
        'twitter:card': { name: 'twitter:card', content: 'summary_large_image' },
        'twitter:title': { name: 'twitter:title', content: 'أكواد | توثيق الموقع' },
        'twitter:description': { name: 'twitter:description', content: 'إدارة ملف عملك التجاري في منصة أكواد لتقييم تجربة المستخدم' },
        'twitter:image': { name: 'twitter:image', content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg' },
        robots: { name: 'robots', content: 'index, follow' },
        viewport: { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        ...(process.env.PROD_ENV === 'demo'
          ? { robots: { name: 'robots', content: 'noindex' } }
          : {}),
      },
      link: {
        canonical: { rel: 'canonical', href: 'https://akwad.sa/verify-account' },
      },
    },
    SearchResultsPage: {
      title: 'أكواد | نتائج البحث',
      meta: {
        description: {
          name: 'description',
          content: 'البحث عن المواقع والخدمات في منصة أكواد لتقييم تجربة المستخدم',
        },
        keywords: {
          name: 'keywords',
          content: 'البحث, نتائج البحث, المواقع, الخدمات, أكواد',
        },
        multi_words: {
          name: 'multi_words',
          content: 'search, results, businesses, akwad',
        },
        'og:title': { property: 'og:title', content: 'أكواد | نتائج البحث' },
        'og:description': {
          property: 'og:description',
          content: 'البحث عن المواقع والخدمات في منصة أكواد لتقييم تجربة المستخدم',
        },
        'og:type': { property: 'og:type', content: 'website' },
        'og:url': { property: 'og:url', content: 'https://akwad.sa/search' },
        'og:image': { property: 'og:image', content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg' },
        'og:locale': { property: 'og:locale', content: 'ar_AR' },
        'og:site_name': { property: 'og:site_name', content: 'أكواد – أداة تقييم تجربة المستخدم' },
        'twitter:card': { name: 'twitter:card', content: 'summary_large_image' },
        'twitter:title': { name: 'twitter:title', content: 'أكواد | نتائج البحث' },
        'twitter:description': { name: 'twitter:description', content: 'البحث عن المواقع والخدمات في منصة أكواد لتقييم تجربة المستخدم' },
        'twitter:image': { name: 'twitter:image', content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg' },
        robots: { name: 'robots', content: 'index, follow' },
        viewport: { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        ...(process.env.PROD_ENV === 'demo'
          ? { robots: { name: 'robots', content: 'noindex' } }
          : {}),
      },
      link: {
        canonical: { rel: 'canonical', href: 'https://akwad.sa/search' },
      },
    },
    ReviewPage: {
      title: 'أكواد | التقييم',
      meta: {
        description: {
          name: 'description',
          content: 'اقرأ واكتب التقييمات على منصة أكواد لتقييم تجربة المستخدم',
        },
        keywords: {
          name: 'keywords',
          content: 'التقييمات, التقييم, التغذية الراجعة, تقييم المواقع, أكواد',
        },
        multi_words: {
          name: 'multi_words',
          content: 'reviews, ratings, feedback, akwad',
        },
        'og:title': { property: 'og:title', content: 'أكواد | التقييم' },
        'og:description': {
          property: 'og:description',
          content: 'اقرأ واكتب التقييمات على منصة أكواد لتقييم تجربة المستخدم',
        },
        'og:type': { property: 'og:type', content: 'website' },
        'og:url': { property: 'og:url', content: 'https://akwad.sa/review' },
        'og:image': { property: 'og:image', content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg' },
        'og:locale': { property: 'og:locale', content: 'ar_AR' },
        'og:site_name': { property: 'og:site_name', content: 'أكواد – أداة تقييم تجربة المستخدم' },
        'twitter:card': { name: 'twitter:card', content: 'summary_large_image' },
        'twitter:title': { name: 'twitter:title', content: 'أكواد | التقييم' },
        'twitter:description': { name: 'twitter:description', content: 'اقرأ واكتب التقييمات على منصة أكواد لتقييم تجربة المستخدم' },
        'twitter:image': { name: 'twitter:image', content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg' },
        robots: { name: 'robots', content: 'index, follow' },
        viewport: { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        ...(process.env.PROD_ENV === 'demo'
          ? { robots: { name: 'robots', content: 'noindex' } }
          : {}),
      },
      link: {
        canonical: { rel: 'canonical', href: 'https://akwad.sa/review' },
      },
    },
    PrivacyPage: {
      title: 'أكواد | سياسة الخصوصية',
      meta: {
        description: {
          name: 'description',
          content: 'اقرأ شروط الخصوصية في منصة أكواد لتقييم تجربة المستخدم',
        },
        keywords: {
          name: 'keywords',
          content: 'الخصوصية, الشروط, الأمان, البيانات, أكواد',
        },
        multi_words: {
          name: 'multi_words',
          content: 'privacy, terms, security, data',
        },
        'og:title': { property: 'og:title', content: 'أكواد | سياسة الخصوصية' },
        'og:description': {
          property: 'og:description',
          content: 'اقرأ شروط الخصوصية في منصة أكواد لتقييم تجربة المستخدم',
        },
        'og:type': { property: 'og:type', content: 'website' },
        'og:url': { property: 'og:url', content: 'https://akwad.sa/privacy' },
        'og:image': { property: 'og:image', content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg' },
        'og:locale': { property: 'og:locale', content: 'ar_AR' },
        'og:site_name': { property: 'og:site_name', content: 'أكواد – أداة تقييم تجربة المستخدم' },
        'twitter:card': { name: 'twitter:card', content: 'summary_large_image' },
        'twitter:title': { name: 'twitter:title', content: 'أكواد | سياسة الخصوصية' },
        'twitter:description': { name: 'twitter:description', content: 'اقرأ شروط الخصوصية في منصة أكواد لتقييم تجربة المستخدم' },
        'twitter:image': { name: 'twitter:image', content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg' },
        robots: { name: 'robots', content: 'index, follow' },
        viewport: { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        ...(process.env.PROD_ENV === 'demo'
          ? { robots: { name: 'robots', content: 'noindex' } }
          : {}),
      },
      link: {
        canonical: { rel: 'canonical', href: 'https://akwad.sa/privacy' },
      },
    },
    TermsPage: {
      title: 'أكواد | سياسة الاستخدام',
      meta: {
        description: {
          name: 'description',
          content: 'اقرأ شروط الاستخدام في منصة أكواد لتقييم تجربة المستخدم',
        },
        keywords: {
          name: 'keywords',
          content: 'شروط الاستخدام, السياسات, القواعد, أكواد',
        },
        multi_words: {
          name: 'multi_words',
          content: 'privacy, terms, security, data',
        },
        'og:title': { property: 'og:title', content: 'أكواد | سياسة الاستخدام' },
        'og:description': {
          property: 'og:description',
          content: 'اقرأ شروط الاستخدام في منصة أكواد لتقييم تجربة المستخدم',
        },
        'og:type': { property: 'og:type', content: 'website' },
        'og:url': { property: 'og:url', content: 'https://akwad.sa/terms' },
        'og:image': { property: 'og:image', content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg' },
        'og:locale': { property: 'og:locale', content: 'ar_AR' },
        'og:site_name': { property: 'og:site_name', content: 'أكواد – أداة تقييم تجربة المستخدم' },
        'twitter:card': { name: 'twitter:card', content: 'summary_large_image' },
        'twitter:title': { name: 'twitter:title', content: 'أكواد | سياسة الاستخدام' },
        'twitter:description': { name: 'twitter:description', content: 'اقرأ شروط الاستخدام في منصة أكواد لتقييم تجربة المستخدم' },
        'twitter:image': { name: 'twitter:image', content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg' },
        robots: { name: 'robots', content: 'index, follow' },
        viewport: { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        ...(process.env.PROD_ENV === 'demo'
          ? { robots: { name: 'robots', content: 'noindex' } }
          : {}),
      },
      link: {
        canonical: { rel: 'canonical', href: 'https://akwad.sa/terms' },
      },
    },
    login: {
      title: 'أكواد | تسجيل الدخول',
      meta: {
        description: {
          name: 'description',
          content: 'سجل دخولك إلى حسابك في أكواد لتقييم ومراجعة المواقع',
        },
        keywords: {
          name: 'keywords',
          content: 'تسجيل الدخول, تسجيل, الحساب, أكواد',
        },
        multi_words: {
          name: 'multi_words',
          content: 'login, signin, account, akwad',
        },
        'og:title': { property: 'og:title', content: 'أكواد | تسجيل الدخول' },
        'og:description': {
          property: 'og:description',
          content: 'سجل دخولك إلى حسابك في أكواد لتقييم ومراجعة المواقع',
        },
        'og:type': { property: 'og:type', content: 'website' },
        'og:url': { property: 'og:url', content: 'https://akwad.sa/login' },
        'og:image': { property: 'og:image', content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg' },
        'og:locale': { property: 'og:locale', content: 'ar_AR' },
        'og:site_name': { property: 'og:site_name', content: 'أكواد – أداة تقييم تجربة المستخدم' },
        'twitter:card': { name: 'twitter:card', content: 'summary_large_image' },
        'twitter:title': { name: 'twitter:title', content: 'أكواد | تسجيل الدخول' },
        'twitter:description': { name: 'twitter:description', content: 'سجل دخولك إلى حسابك في أكواد لتقييم ومراجعة المواقع' },
        'twitter:image': { name: 'twitter:image', content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg' },
        robots: { name: 'robots', content: 'index, follow' },
        viewport: { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        ...(process.env.PROD_ENV === 'demo'
          ? { robots: { name: 'robots', content: 'noindex' } }
          : {}),
      },
      link: {
        canonical: { rel: 'canonical', href: 'https://akwad.sa/login' },
      },
    },
    register: {
      title: 'أكواد | إنشاء حساب',
      meta: {
        description: {
          name: 'description',
          content: 'أنشئ حساباً جديداً في أكواد لبدء تقييم ومراجعة المواقع',
        },
        keywords: {
          name: 'keywords',
          content: 'إنشاء حساب, التسجيل, حساب جديد, أكواد',
        },
        multi_words: {
          name: 'multi_words',
          content: 'register, signup, create account, akwad',
        },
        'og:title': { property: 'og:title', content: 'أكواد | إنشاء حساب' },
        'og:description': {
          property: 'og:description',
          content: 'أنشئ حساباً جديداً في أكواد لبدء تقييم ومراجعة المواقع',
        },
        'og:type': { property: 'og:type', content: 'website' },
        'og:url': { property: 'og:url', content: 'https://akwad.sa/register' },
        'og:image': { property: 'og:image', content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg' },
        'og:locale': { property: 'og:locale', content: 'ar_AR' },
        'og:site_name': { property: 'og:site_name', content: 'أكواد – أداة تقييم تجربة المستخدم' },
        'twitter:card': { name: 'twitter:card', content: 'summary_large_image' },
        'twitter:title': { name: 'twitter:title', content: 'أكواد | إنشاء حساب' },
        'twitter:description': { name: 'twitter:description', content: 'أنشئ حساباً جديداً في أكواد لبدء تقييم ومراجعة المواقع' },
        'twitter:image': { name: 'twitter:image', content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg' },
        robots: { name: 'robots', content: 'index, follow' },
        viewport: { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        ...(process.env.PROD_ENV === 'demo'
          ? { robots: { name: 'robots', content: 'noindex' } }
          : {}),
      },
      link: {
        canonical: { rel: 'canonical', href: 'https://akwad.sa/register' },
      },
    },
    BusinessProfilePage: {
      title: 'أكواد | قائمة المواقع',
      meta: {
        description: {
          name: 'description',
          content: 'إدارة مواقعك ومراقبة أدائها في منصة أكواد لتقييم تجربة المستخدم',
        },
        keywords: {
          name: 'keywords',
          content: 'الملف التجاري, إدارة المواقع, مراقبة الأداء, أكواد',
        },
        multi_words: {
          name: 'multi_words',
          content: 'business profile, website management, performance monitoring, akwad',
        },
        'og:title': { property: 'og:title', content: 'أكواد | قائمة المواقع' },
        'og:description': {
          property: 'og:description',
          content: 'إدارة مواقعك ومراقبة أدائها في منصة أكواد لتقييم تجربة المستخدم',
        },
        'og:type': { property: 'og:type', content: 'website' },
        'og:url': { property: 'og:url', content: 'https://akwad.sa/business-profile' },
        'og:image': { property: 'og:image', content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg' },
        'og:locale': { property: 'og:locale', content: 'ar_AR' },
        'og:site_name': { property: 'og:site_name', content: 'أكواد – أداة تقييم تجربة المستخدم' },
        'twitter:card': { name: 'twitter:card', content: 'summary_large_image' },
        'twitter:title': { name: 'twitter:title', content: 'أكواد | قائمة المواقع' },
        'twitter:description': { name: 'twitter:description', content: 'إدارة مواقعك ومراقبة أدائها في منصة أكواد لتقييم تجربة المستخدم' },
        'twitter:image': { name: 'twitter:image', content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg' },
        robots: { name: 'robots', content: 'index, follow' },
        viewport: { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        ...(process.env.PROD_ENV === 'demo'
          ? { robots: { name: 'robots', content: 'noindex' } }
          : {}),
      },
      link: {
        canonical: { rel: 'canonical', href: 'https://akwad.sa/business-profile' },
      },
    },
    'verify-account': {
      title: 'أكواد | تأكيد الحساب',
      meta: {
        description: {
          name: 'description',
          content: 'أكد حسابك في أكواد للوصول إلى جميع الميزات',
        },
        keywords: {
          name: 'keywords',
          content: 'تأكيد الحساب, التأكيد, التحقق من الهاتف, أكواد',
        },
        multi_words: {
          name: 'multi_words',
          content: 'verify account, confirmation, phone verification, akwad',
        },
        'og:title': { property: 'og:title', content: 'أكواد | تأكيد الحساب' },
        'og:description': {
          property: 'og:description',
          content: 'أكد حسابك في أكواد للوصول إلى جميع الميزات',
        },
        'og:type': { property: 'og:type', content: 'website' },
        'og:url': { property: 'og:url', content: 'https://akwad.sa/verify-account' },
        'og:image': { property: 'og:image', content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg' },
        'og:locale': { property: 'og:locale', content: 'ar_AR' },
        'og:site_name': { property: 'og:site_name', content: 'أكواد – أداة تقييم تجربة المستخدم' },
        'twitter:card': { name: 'twitter:card', content: 'summary_large_image' },
        'twitter:title': { name: 'twitter:title', content: 'أكواد | تأكيد الحساب' },
        'twitter:description': { name: 'twitter:description', content: 'أكد حسابك في أكواد للوصول إلى جميع الميزات' },
        'twitter:image': { name: 'twitter:image', content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg' },
        robots: { name: 'robots', content: 'index, follow' },
        viewport: { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        ...(process.env.PROD_ENV === 'demo'
          ? { robots: { name: 'robots', content: 'noindex' } }
          : {}),
      },
      link: {
        canonical: { rel: 'canonical', href: 'https://akwad.sa/verify-account' },
      },
    },
    'forgot-password': {
      title: 'أكواد | استعادة كلمة المرور',
      meta: {
        description: {
          name: 'description',
          content: 'أعد تعيين كلمة المرور واستعد الوصول إلى حسابك في أكواد',
        },
        keywords: {
          name: 'keywords',
          content: 'نسيت كلمة المرور, إعادة تعيين كلمة المرور, استعادة الحساب, أكواد',
        },
        multi_words: {
          name: 'multi_words',
          content: 'forgot password, reset password, account recovery, akwad',
        },
        'og:title': { property: 'og:title', content: 'أكواد | استعادة كلمة المرور' },
        'og:description': {
          property: 'og:description',
          content: 'أعد تعيين كلمة المرور واستعد الوصول إلى حسابك في أكواد',
        },
        'og:type': { property: 'og:type', content: 'website' },
        'og:url': { property: 'og:url', content: 'https://akwad.sa/forgot-password' },
        'og:image': { property: 'og:image', content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg' },
        'og:locale': { property: 'og:locale', content: 'ar_AR' },
        'og:site_name': { property: 'og:site_name', content: 'أكواد – أداة تقييم تجربة المستخدم' },
        'twitter:card': { name: 'twitter:card', content: 'summary_large_image' },
        'twitter:title': { name: 'twitter:title', content: 'أكواد | استعادة كلمة المرور' },
        'twitter:description': { name: 'twitter:description', content: 'أعد تعيين كلمة المرور واستعد الوصول إلى حسابك في أكواد' },
        'twitter:image': { name: 'twitter:image', content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg' },
        robots: { name: 'robots', content: 'index, follow' },
        viewport: { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        ...(process.env.PROD_ENV === 'demo'
          ? { robots: { name: 'robots', content: 'noindex' } }
          : {}),
      },
      link: {
        canonical: { rel: 'canonical', href: 'https://akwad.sa/forgot-password' },
      },
    },
    OrdersPage: {
      title: 'أكواد | للأعمال',
      meta: {
        description: {
          name: 'description',
          content: 'اطلب خدمات تحليل التجربة الرقمية واختبار المستخدم من أكواد',
        },
        keywords: {
          name: 'keywords',
          content: 'الخدمات الرقمية, اختبار المستخدم, تحليل التجربة, حلول الأعمال, أكواد',
        },
        multi_words: {
          name: 'multi_words',
          content: 'digital services, user testing, experience analysis, business solutions, akwad',
        },
        'og:title': { property: 'og:title', content: 'أكواد | للأعمال' },
        'og:description': {
          property: 'og:description',
          content: 'اطلب خدمات تحليل التجربة الرقمية واختبار المستخدم من أكواد',
        },
        'og:type': { property: 'og:type', content: 'website' },
        'og:url': { property: 'og:url', content: 'https://akwad.sa/orders' },
        'og:image': { property: 'og:image', content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg' },
        'og:locale': { property: 'og:locale', content: 'ar_AR' },
        'og:site_name': { property: 'og:site_name', content: 'أكواد – أداة تقييم تجربة المستخدم' },
        'twitter:card': { name: 'twitter:card', content: 'summary_large_image' },
        'twitter:title': { name: 'twitter:title', content: 'أكواد | للأعمال' },
        'twitter:description': { name: 'twitter:description', content: 'اطلب خدمات تحليل التجربة الرقمية واختبار المستخدم من أكواد' },
        'twitter:image': { name: 'twitter:image', content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg' },
        robots: { name: 'robots', content: 'index, follow' },
        viewport: { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        ...(process.env.PROD_ENV === 'demo'
          ? { robots: { name: 'robots', content: 'noindex' } }
          : {}),
      },
      link: {
        canonical: { rel: 'canonical', href: 'https://akwad.sa/orders' },
      },
    },
    ProfileSettingsPage: {
      title: 'أكواد | إعدادات الحساب',
      meta: {
        description: {
          name: 'description',
          content: 'خصص إعدادات حسابك وتفضيلاتك في أكواد',
        },
        keywords: {
          name: 'keywords',
          content: 'إعدادات الحساب, تفضيلات الملف الشخصي, إعدادات الأمان, أكواد',
        },
        multi_words: {
          name: 'multi_words',
          content: 'account settings, profile preferences, security settings, akwad',
        },
        'og:title': { property: 'og:title', content: 'أكواد | إعدادات الحساب' },
        'og:description': {
          property: 'og:description',
          content: 'خصص إعدادات حسابك وتفضيلاتك في أكواد',
        },
        'og:type': { property: 'og:type', content: 'website' },
        'og:url': { property: 'og:url', content: 'https://akwad.sa/profile-settings' },
        'og:image': { property: 'og:image', content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg' },
        'og:locale': { property: 'og:locale', content: 'ar_AR' },
        'og:site_name': { property: 'og:site_name', content: 'أكواد – أداة تقييم تجربة المستخدم' },
        'twitter:card': { name: 'twitter:card', content: 'summary_large_image' },
        'twitter:title': { name: 'twitter:title', content: 'أكواد | إعدادات الحساب' },
        'twitter:description': { name: 'twitter:description', content: 'خصص إعدادات حسابك وتفضيلاتك في أكواد' },
        'twitter:image': { name: 'twitter:image', content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg' },
        robots: { name: 'robots', content: 'index, follow' },
        viewport: { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        ...(process.env.PROD_ENV === 'demo'
          ? { robots: { name: 'robots', content: 'noindex' } }
          : {}),
      },
      link: {
        canonical: { rel: 'canonical', href: 'https://akwad.sa/profile-settings' },
      },
    },
    ResultDetailsPage: {
      title: 'أكواد | تقرير تجربة المستخدم الرقمية',
      meta: {
        description: {
          name: 'description',
          content: 'تحليل مفصل ورؤى حول أداء موقعك وتجربة المستخدم ومقاييس تحسين محركات البحث في أكواد',
        },
        keywords: {
          name: 'keywords',
          content: 'التقرير المفصل, تحليل الموقع, مقاييس الأداء, تجربة المستخدم, تحليل تحسين محركات البحث, أكواد',
        },
        multi_words: {
          name: 'multi_words',
          content: 'detailed report, website analysis, performance metrics, user experience, SEO analysis, akwad',
        },
        'og:title': { property: 'og:title', content: 'أكواد | تقرير تجربة المستخدم الرقمية' },
        'og:description': {
          property: 'og:description',
          content: 'تحليل مفصل ورؤى حول أداء موقعك وتجربة المستخدم ومقاييس تحسين محركات البحث في أكواد',
        },
        'og:type': { property: 'og:type', content: 'website' },
        'og:url': { property: 'og:url', content: 'https://akwad.sa/result-details' },
        'og:image': { property: 'og:image', content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg' },
        'og:locale': { property: 'og:locale', content: 'ar_AR' },
        'og:site_name': { property: 'og:site_name', content: 'أكواد – أداة تقييم تجربة المستخدم' },
        'twitter:card': { name: 'twitter:card', content: 'summary_large_image' },
        'twitter:title': { name: 'twitter:title', content: 'أكواد | تقرير تجربة المستخدم الرقمية' },
        'twitter:description': { name: 'twitter:description', content: 'تحليل مفصل ورؤى حول أداء موقعك وتجربة المستخدم ومقاييس تحسين محركات البحث في أكواد' },
        'twitter:image': { name: 'twitter:image', content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg' },
        robots: { name: 'robots', content: 'index, follow' },
        viewport: { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        ...(process.env.PROD_ENV === 'demo'
          ? { robots: { name: 'robots', content: 'noindex' } }
          : {}),
      },
      link: {
        canonical: { rel: 'canonical', href: 'https://akwad.sa/result-details' },
      },
    },
    AddRatingPage: {
      title: 'أكواد | إضافة تقييم',
      meta: {
        description: {
          name: 'description',
          content: 'شارك تجربتك وقيم المواقع في منصة أكواد',
        },
        keywords: {
          name: 'keywords',
          content: 'إضافة تقييم, تقييم الموقع, تغذية راجعة المستخدم, تقييم المواقع, أكواد',
        },
        multi_words: {
          name: 'multi_words',
          content: 'add review, rate website, user feedback, website rating, akwad',
        },
        'og:title': { property: 'og:title', content: 'أكواد | إضافة تقييم' },
        'og:description': {
          property: 'og:description',
          content: 'شارك تجربتك وقيم المواقع في منصة أكواد',
        },
        'og:type': { property: 'og:type', content: 'website' },
        'og:url': { property: 'og:url', content: 'https://akwad.sa/add-rating' },
        'og:image': { property: 'og:image', content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg' },
        'og:locale': { property: 'og:locale', content: 'ar_AR' },
        'og:site_name': { property: 'og:site_name', content: 'أكواد – أداة تقييم تجربة المستخدم' },
        'twitter:card': { name: 'twitter:card', content: 'summary_large_image' },
        'twitter:title': { name: 'twitter:title', content: 'أكواد | إضافة تقييم' },
        'twitter:description': { name: 'twitter:description', content: 'شارك تجربتك وقيم المواقع في منصة أكواد' },
        'twitter:image': { name: 'twitter:image', content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg' },
        robots: { name: 'robots', content: 'index, follow' },
        viewport: { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        ...(process.env.PROD_ENV === 'demo'
          ? { robots: { name: 'robots', content: 'noindex' } }
          : {}),
      },
      link: {
        canonical: { rel: 'canonical', href: 'https://akwad.sa/add-rating' },
      },
    },
    EditRatingPage: {
      title: 'أكواد | تعديل التقييم',
      meta: {
        description: {
          name: 'description',
          content: 'حدث تقييمك وتقييم موقعك في منصة أكواد',
        },
        keywords: {
          name: 'keywords',
          content: 'تعديل التقييم, تحديث التقييم, تعديل التغذية الراجعة, تقييم المواقع, أكواد',
        },
        multi_words: {
          name: 'multi_words',
          content: 'edit review, update rating, modify feedback, website rating, akwad',
        },
        'og:title': { property: 'og:title', content: 'أكواد | تعديل التقييم' },
        'og:description': {
          property: 'og:description',
          content: 'حدث تقييمك وتقييم موقعك في منصة أكواد',
        },
        'og:type': { property: 'og:type', content: 'website' },
        'og:url': { property: 'og:url', content: 'https://akwad.sa/edit-rating' },
        'og:image': { property: 'og:image', content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg' },
        'og:locale': { property: 'og:locale', content: 'ar_AR' },
        'og:site_name': { property: 'og:site_name', content: 'أكواد – أداة تقييم تجربة المستخدم' },
        'twitter:card': { name: 'twitter:card', content: 'summary_large_image' },
        'twitter:title': { name: 'twitter:title', content: 'أكواد | تعديل التقييم' },
        'twitter:description': { name: 'twitter:description', content: 'حدث تقييمك وتقييم موقعك في منصة أكواد' },
        'twitter:image': { name: 'twitter:image', content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg' },
        robots: { name: 'robots', content: 'index, follow' },
        viewport: { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        ...(process.env.PROD_ENV === 'demo'
          ? { robots: { name: 'robots', content: 'noindex' } }
          : {}),
      },
      link: {
        canonical: { rel: 'canonical', href: 'https://akwad.sa/edit-rating' },
      },
    },
 
  };

  if (route.params.url && route.name === 'ResultPage') {
    try {
      const resMeta = await getReportMetaContent(route.params.url as string);
      return {
        title: `${resMeta.data.page_title} - أكواد`,
        meta: {
          description: {
            name: 'description',
            content: resMeta.data.description,
          },
          keywords: {
            name: 'keywords',
            content: `${resMeta.data.keywords}, أكواد`,
          },
          multi_words: {
            name: 'multi_words',
            content: `${resMeta.data.multi_words}, akwad`,
          },
          'og:title': {
            property: 'og:title',
            content: `${resMeta.data.title} - أكواد`,
          },
          'og:description': {
            property: 'og:description',
            content: resMeta.data.description,
          },
          'og:type': { property: 'og:type', content: 'website' },
          'og:url': { property: 'og:url', content: `https://akwad.sa/result/${route.params.url}` },
          'og:image': { property: 'og:image', content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg' },
          'og:locale': { property: 'og:locale', content: 'ar_AR' },
          'og:site_name': { property: 'og:site_name', content: 'أكواد – أداة تقييم تجربة المستخدم' },
          'twitter:card': { name: 'twitter:card', content: 'summary_large_image' },
          'twitter:title': { name: 'twitter:title', content: `${resMeta.data.title} - أكواد` },
          'twitter:description': { name: 'twitter:description', content: resMeta.data.description },
          'twitter:image': { name: 'twitter:image', content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg' },
          robots: { name: 'robots', content: 'index, follow' },
          viewport: { name: 'viewport', content: 'width=device-width, initial-scale=1' },
          ...(process.env.PROD_ENV === 'demo'
            ? { robots: { name: 'robots', content: 'noindex' } }
            : {}),
        },
        link: {
          canonical: { rel: 'canonical', href: `https://akwad.sa/result/${route.params.url}` },
        },
      };
    } catch (error) {
      console.error('Failed to fetch meta tags:', error);
    }
  }

  return (
    metaTags[route.name as string] || {
      title: 'أكواد – أداة تقييم تجربة المستخدم',
      meta: {
        description: { name: 'description', content: 'أكواد أداة سعودية متخصصة في تقييم تجربة المستخدم للمواقع والمنتجات الرقمية' },
        keywords: { name: 'keywords', content: 'أكواد, تقييم تجربة المستخدم, منصة التقييم' },
        multi_words: {
          name: 'multi_words',
          content: 'akwad, rating, platform',
        },
        'og:title': { property: 'og:title', content: 'أكواد – أداة تقييم تجربة المستخدم' },
        'og:description': {
          property: 'og:description',
          content: 'أكواد أداة سعودية متخصصة في تقييم تجربة المستخدم للمواقع والمنتجات الرقمية',
        },
        'og:type': { property: 'og:type', content: 'website' },
        'og:url': { property: 'og:url', content: 'https://akwad.sa/' },
        'og:image': { property: 'og:image', content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg' },
        'og:locale': { property: 'og:locale', content: 'ar_AR' },
        'og:site_name': { property: 'og:site_name', content: 'أكواد – أداة تقييم تجربة المستخدم' },
        'twitter:card': { name: 'twitter:card', content: 'summary_large_image' },
        'twitter:title': { name: 'twitter:title', content: 'أكواد – أداة تقييم تجربة المستخدم' },
        'twitter:description': { name: 'twitter:description', content: 'أكواد أداة سعودية متخصصة في تقييم تجربة المستخدم للمواقع والمنتجات الرقمية' },
        'twitter:image': { name: 'twitter:image', content: 'https://akwad.sa/assets/logo-CEm7vz4L.svg' },
        robots: { name: 'robots', content: 'index, follow' },
        viewport: { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        ...(process.env.PROD_ENV === 'demo'
          ? { robots: { name: 'robots', content: 'noindex' } }
          : {}),
      },
      link: {
        canonical: { rel: 'canonical', href: 'https://akwad.sa/' },
      },
    }
  );
};
